import { useCallback, useMemo, useRef, useState } from "react";

import type { AssistantPaymentCard } from "../PaymentCards/helpers/parsePaymentCards";
import type { PaymentChoice } from "../PaymentCards/PaymentCards";

import { DONOR_ASSISTANT_CONFIG } from "./donorAssistantConfig";
import { createDonorAssistantTransport } from "./donorAssistantTransport";
import { buildPaymentReferencePart } from "./paymentReference";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import type { AssistantChatInitial } from "@/features/Assistant/helpers/types";
import { useAssistantChat } from "@/features/Assistant/helpers/useAssistantChat";

export const useDonorAssistant = (initial?: AssistantChatInitial) => {
  const utils = trpc.useUtils();
  const aiSessionId = initial?.aiSessionId;
  const onSessionCreated = initial?.onSessionCreated;
  const { locale } = useLocaleContext();
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const transport = useMemo(
    () =>
      createDonorAssistantTransport(utils.client, {
        aiSessionId,
        onSessionCreated,
        getStreamContext: () => ({ locale: localeRef.current }),
      }),
    [utils.client, aiSessionId, onSessionCreated]
  );
  const [referencedPayment, setReferencedPayment] = useState<AssistantPaymentCard | null>(null);

  const chat = useAssistantChat({
    transport,
    initial,
    config: DONOR_ASSISTANT_CONFIG,
    buildMessageParts: (text) =>
      referencedPayment
        ? [{ type: "text", text }, buildPaymentReferencePart(referencedPayment.id)]
        : [{ type: "text", text }],
    onToolRendered: initial?.onToolRendered,
  });
  const { isComposerDisabled, sendTurn } = chat;

  const choosePayment = useCallback(
    ({ payment, text }: PaymentChoice) => {
      if (isComposerDisabled) {
        return;
      }
      setReferencedPayment(payment);
      sendTurn([{ type: "text", text }, buildPaymentReferencePart(payment.id)]);
    },
    [sendTurn, isComposerDisabled]
  );

  return {
    ...chat,
    choosePayment,
    referencedPayment,
    askAboutPayment: setReferencedPayment,
    clearReferencedPayment: () => setReferencedPayment(null),
  };
};

export type DonorAssistantChat = ReturnType<typeof useDonorAssistant>;
